import { useCallback, useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { bindGameResync } from "../../../utils/bindGameResync";
import { useHolSessionCountdown } from "../../games/HolSessionCountdown";
import useGameSounds from "../../../hooks/useGameSounds";
import { pauseBgMusic, playSound } from "../../../utils/gameAudio";
import { playClueNote } from "../../../utils/audioClue";
import {
  HOL_HOT_SEAT_CLAP_URL,
  HOL_CARD_FLIP_URL,
  playHolSound,
} from "../../../constants/holDefaultSounds";

const PREP_BG = "prep_bg";
const BUILDING_MUSIC = "building_user_music";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";
const SP_BASE = `${API_BASE}/api/higher-or-lower/admin/single-player`;
const MYM_CONFIG_URL = `${API_BASE}/api/settings/public/mym-titles-config`;
const SETUP_POLL_MS = 8000;

const getAuthToken = () => localStorage.getItem("auth_token");
const authHeader = () => ({ Authorization: `Bearer ${getAuthToken()}` });
const jsonHeaders = () => ({ "Content-Type": "application/json", ...authHeader() });

const emptyResults = (sessionId) => ({
  session_id: sessionId || null,
  leaderboard: [],
  hot_seat_runs: 0,
  players_joined: 0,
  rounds_per_player: 0,
});

/** Play with Host (Higher or Lower) — session state, socket sync and host actions. */
export const useHolHostSession = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [joiners, setJoiners] = useState([]);
  const [history, setHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [numberOfCards, setNumberOfCards] = useState(5);
  const [forceActive, setForceActive] = useState(false);
  const [hotSeatAnnounce, setHotSeatAnnounce] = useState(null);
  const [clueLoading, setClueLoading] = useState(false);
  const [finalResults, setFinalResults] = useState(null);

  const socketRef = useRef(null);
  const sessionIdRef = useRef(null);
  const dismissedResultsRef = useRef(new Set());
  const { sounds, play, playLoop, stopBgMusic } = useGameSounds("higher_or_lower");
  const soundsRef = useRef(sounds);
  const playRef = useRef(play);
  const playLoopRef = useRef(playLoop);
  const stopBgRef = useRef(stopBgMusic);
  /** Bumps whenever a newer cue should ignore a one-shot's "then loop" ending. */
  const cueGenRef = useRef(0);
  const oneShotRef = useRef(null);
  const spinAudioRef = useRef(null);
  const loopKeyRef = useRef(null);
  /** Loop requested before its file had loaded. Started once the sound map arrives. */
  const pendingLoopRef = useRef(null);
  const musicRef = useRef({});
  const prevRevealedRef = useRef(null);
  const prevResultRef = useRef(null);
  const { countdownEndsAt, applyCountdown, clearCountdown } = useHolSessionCountdown();
  const applyCountdownRef = useRef(applyCountdown);
  applyCountdownRef.current = applyCountdown;
  const loadFlightRef = useRef(null);

  useEffect(() => {
    soundsRef.current = sounds;
  }, [sounds]);

  useEffect(() => {
    playRef.current = play;
  }, [play]);

  useEffect(() => {
    playLoopRef.current = playLoop;
  }, [playLoop]);

  useEffect(() => {
    stopBgRef.current = stopBgMusic;
  }, [stopBgMusic]);

  const stopSpinAudio = useCallback(() => {
    const audio = spinAudioRef.current;
    spinAudioRef.current = null;
    if (!audio) return;
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {
      /* ignore */
    }
  }, []);

  const stopOneShot = useCallback(() => {
    const audio = oneShotRef.current;
    oneShotRef.current = null;
    if (!audio) return;
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {
      /* ignore */
    }
  }, []);

  const startLoopNow = useCallback((actionKey) => {
    loopKeyRef.current = actionKey;
    if (!soundsRef.current[actionKey]?.audio_url) {
      pendingLoopRef.current = actionKey;
      stopBgRef.current();
      return;
    }
    pendingLoopRef.current = null;
    playLoopRef.current(actionKey);
  }, []);

  const beginLoop = useCallback((actionKey) => {
    if (loopKeyRef.current === actionKey && !oneShotRef.current) return;
    cueGenRef.current += 1;
    stopOneShot();
    startLoopNow(actionKey);
  }, [stopOneShot, startLoopNow]);

  /** Pause the current loop, play a one-shot, then start `loopKey` from the top. */
  const playOnceThenLoop = useCallback((onceKey, loopKey) => {
    const gen = ++cueGenRef.current;
    stopOneShot();
    const startLoop = () => {
      if (cueGenRef.current !== gen) return;
      oneShotRef.current = null;
      startLoopNow(loopKey);
    };
    const cfg = soundsRef.current[onceKey];
    if (!cfg?.audio_url) {
      if (loopKeyRef.current !== loopKey) startLoop();
      return;
    }
    pauseBgMusic();
    const audio = playSound(cfg.audio_url, { volume: cfg.volume ?? 1, loop: false });
    if (!audio) {
      startLoop();
      return;
    }
    oneShotRef.current = audio;
    const finish = () => {
      audio.removeEventListener("ended", finish);
      audio.removeEventListener("error", finish);
      if (oneShotRef.current !== audio) return;
      startLoop();
    };
    audio.addEventListener("ended", finish);
    audio.addEventListener("error", finish);
  }, [stopOneShot, startLoopNow]);

  const notifyPrepMusic = useCallback((active) => {
    return fetch(`${SP_BASE}/prep-music`, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({ active: !!active }),
    }).catch(() => null);
  }, []);

  const stopHostMusic = useCallback(() => {
    cueGenRef.current += 1;
    stopOneShot();
    stopSpinAudio();
    loopKeyRef.current = null;
    pendingLoopRef.current = null;
    stopBgRef.current();
    notifyPrepMusic(false);
  }, [stopOneShot, stopSpinAudio, notifyPrepMusic]);

  useEffect(() => {
    const pending = pendingLoopRef.current;
    if (!pending || loopKeyRef.current !== pending) return;
    if (!sounds[pending]?.audio_url) return;
    pendingLoopRef.current = null;
    playLoop(pending);
  }, [sounds, playLoop]);

  const onWheelAction = useCallback((path) => {
    if (path === "build-user-wheel") beginLoop(BUILDING_MUSIC);
    else if (path === "user-wheel-countdown") playOnceThenLoop("countdown_wheel", PREP_BG);
    else if (path === "cancel-user-wheel") beginLoop(PREP_BG);
    else if (path === "spin-user-wheel") {
      if (oneShotRef.current) beginLoop(PREP_BG);
      stopSpinAudio();
      const cfg = soundsRef.current.spinning_sound;
      if (!cfg?.audio_url) return;
      spinAudioRef.current = playSound(cfg.audio_url, {
        volume: cfg.volume ?? 1,
        loop: false,
      });
    }
  }, [beginLoop, playOnceThenLoop, stopSpinAudio]);

  const playHotSeatClap = useCallback(() => {
    stopSpinAudio();
    if (loopKeyRef.current === BUILDING_MUSIC) beginLoop(PREP_BG);
    playHolSound(soundsRef.current, playRef.current, "hot_seat", HOL_HOT_SEAT_CLAP_URL);
  }, [beginLoop, stopSpinAudio]);

  useEffect(() => {
    musicRef.current = {
      beginPrep: () => {
        beginLoop(PREP_BG);
        return notifyPrepMusic(true);
      },
      onStart: () => playOnceThenLoop("start_session_countdown", PREP_BG),
      onWheelAction,
      stop: stopHostMusic,
    };
  }, [beginLoop, playOnceThenLoop, onWheelAction, stopHostMusic, notifyPrepMusic]);

  useEffect(() => {
    if (!session?.id) return;
    notifyPrepMusic(true);
  }, [session?.id, notifyPrepMusic]);

  useEffect(() => () => {
    cueGenRef.current += 1;
    stopOneShot();
    stopSpinAudio();
  }, [stopOneShot, stopSpinAudio]);

  const playCardFlip = useCallback(() => {
    playHolSound(soundsRef.current, playRef.current, "card_flip", HOL_CARD_FLIP_URL);
  }, []);

  useEffect(() => {
    const sid = session?.id ?? null;
    const count = session?.revealed_count ?? 0;
    if (!sid) {
      prevRevealedRef.current = null;
      return;
    }
    const prev = prevRevealedRef.current;
    prevRevealedRef.current = { sid, count };
    if (prev && prev.sid === sid && count > prev.count) playCardFlip();
  }, [session?.id, session?.revealed_count, playCardFlip]);

  useEffect(() => {
    const sid = session?.id ?? null;
    if (!sid) {
      prevResultRef.current = null;
      return;
    }
    const result = session?.last_result || null;
    const key = `${sid}-${session?.revealed_count ?? 0}-${result}`;
    const prev = prevResultRef.current;
    prevResultRef.current = key;
    if (prev === null || prev === key) return;
    if (result === "correct" || result === "tie") playRef.current("correct");
    else if (result === "wrong" || result === "incorrect") playRef.current("wrong");
  }, [session?.id, session?.last_result, session?.revealed_count]);

  const clearLiveState = useCallback(() => {
    setSession(null);
    setJoiners([]);
    setHistory([]);
    setLeaderboard([]);
    setHotSeatAnnounce(null);
  }, []);

  const loadSession = useCallback(() => {
    // Share one in-flight fetch. Reveal, guess, and the socket snapshot all
    // ask for this at once; stacking them is what kept the button on "loading".
    if (loadFlightRef.current) return loadFlightRef.current;

    const run = (async () => {
      try {
        const res = await fetch(`${SP_BASE}/session`, { headers: authHeader() });
        if (!res.ok) throw new Error("Failed to load host panel");
        const data = await res.json();
        setSession(data.session || null);
        setJoiners(data.joiners || []);
        setHistory(data.history || []);
        setLeaderboard(data.leaderboard || []);
        if (data.countdown) applyCountdownRef.current(data.countdown);
        setError(null);
        // Do not emit holsp:join-session here. The server answers every join
        // with holsp:state_updated, and that event calls loadSession again.
        return data.session || null;
      } catch (e) {
        setError(e.message);
        return null;
      } finally {
        setLoading(false);
        loadFlightRef.current = null;
      }
    })();

    loadFlightRef.current = run;
    return run;
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/settings/higher_or_lower_cards`, {
        headers: authHeader(),
      });
      if (res.ok) {
        const data = await res.json();
        setNumberOfCards(parseInt(data.setting_value, 10) || 5);
      }
    } catch {
      setNumberOfCards(5);
    }
  }, []);

  const loadForceState = useCallback(async () => {
    try {
      const res = await fetch(MYM_CONFIG_URL);
      const data = await res.json().catch(() => ({}));
      const hol = Array.isArray(data.config) && data.config.find((c) => c.id === "higher_or_lower");
      setForceActive(!!hol?.forceModal);
    } catch {
      setForceActive(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadSession();
    loadForceState();
  }, [loadConfig, loadSession, loadForceState]);

  // While in the setup flow, pick up a session started from another admin screen.
  useEffect(() => {
    if (session?.id) return undefined;
    const t = setInterval(loadSession, SETUP_POLL_MS);
    return () => clearInterval(t);
  }, [session?.id, loadSession]);

  useEffect(() => {
    sessionIdRef.current = session?.id ?? null;
    if (socketRef.current?.connected && sessionIdRef.current) {
      socketRef.current.emit("holsp:join-session", { sessionId: sessionIdRef.current });
    }
  }, [session?.id]);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ["websocket"] });
    socketRef.current = socket;
    socket.on("connect", () => {
      if (sessionIdRef.current) {
        socket.emit("holsp:join-session", { sessionId: sessionIdRef.current });
      }
      loadSession();
      loadForceState();
    });
    const unbindResync = bindGameResync(() => {
      if (socket.connected && sessionIdRef.current) {
        socket.emit("holsp:join-session", { sessionId: sessionIdRef.current });
      }
      loadSession();
      loadForceState();
    });
    socket.on("holsp:state_updated", loadSession);
    socket.on("holsp:joiners_updated", loadSession);
    socket.on("holsp:session_countdown", (payload) => applyCountdownRef.current(payload));
    socket.on("holsp:hot_seat_selected", (payload) => {
      setHotSeatAnnounce({
        display_name: payload?.display_name || "Player",
        profile_picture: payload?.profile_picture || null,
      });
      playHotSeatClap();
      loadSession();
    });
    socket.on("holsp:session_restarted", ({ session_id: newSessionId }) => {
      const oldSid = sessionIdRef.current;
      if (oldSid && oldSid !== newSessionId) {
        socket.emit("holsp:leave-session", { sessionId: oldSid });
      }
      if (newSessionId) {
        sessionIdRef.current = newSessionId;
        socket.emit("holsp:join-session", { sessionId: newSessionId });
      }
      setHotSeatAnnounce(null);
      loadSession();
    });
    socket.on("holsp:session_ended", (payload) => {
      musicRef.current.stop?.();
      const endedId = payload?.session_id ?? null;
      if (!dismissedResultsRef.current.has(endedId)) {
        setFinalResults((prev) =>
          prev && prev.session_id === endedId ? prev : payload?.final_results || emptyResults(endedId),
        );
      }
      clearLiveState();
      clearCountdown();
      loadSession();
    });
    socket.on("mym.force_open", loadForceState);
    socket.on("mym.force_clear", loadForceState);
    return () => {
      unbindResync();
      if (sessionIdRef.current) {
        socket.emit("holsp:leave-session", { sessionId: sessionIdRef.current });
      }
      socket.disconnect();
      socketRef.current = null;
    };
  }, [loadSession, loadForceState, playHotSeatClap, clearLiveState, clearCountdown]);

  /** POST an admin single-player action; returns the response body or null on failure. */
  const action = useCallback(
    async (path, body = {}) => {
      setBusy(true);
      setError(null);
      try {
        const res = await fetch(`${SP_BASE}/${path}`, {
          method: "POST",
          headers: jsonHeaders(),
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Action failed");
        if (data.countdown) applyCountdownRef.current(data.countdown);
        if (data?.success) {
          if (path === "start") musicRef.current.onStart?.();
          else musicRef.current.onWheelAction?.(path);
        }
        await loadSession();
        return data;
      } catch (e) {
        setError(e.message);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [loadSession],
  );

  /** Tell waiting players which setup screen to show. No-op once a session exists. */
  const setLobbyPhase = useCallback(async (phase) => {
    try {
      await fetch(`${SP_BASE}/lobby-phase`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ phase }),
      });
    } catch {
      /* players fall back to the intro until the next update */
    }
  }, []);

  const startSession = useCallback(async (opts = {}) => {
    return action("start", {
      total_cards: numberOfCards,
      clue_max_plays: opts.clue_max_plays,
      clue_user_can_play: opts.clue_user_can_play,
    });
  }, [action, numberOfCards]);

  const writeMymConfig = useCallback(
    async (mapItem) => {
      const getRes = await fetch(MYM_CONFIG_URL, { headers: authHeader() });
      const getData = await getRes.json().catch(() => ({}));
      if (!getRes.ok || !Array.isArray(getData.config)) {
        throw new Error("Failed to load MYM titles config");
      }
      const putRes = await fetch(`${API_BASE}/api/settings/mym-titles-config`, {
        method: "PUT",
        headers: jsonHeaders(),
        body: JSON.stringify({ config: getData.config.map(mapItem) }),
      });
      const putData = await putRes.json().catch(() => ({}));
      if (!putRes.ok) {
        throw new Error(putData.message || putData.error || "Failed to update MYM force");
      }
      await loadForceState();
    },
    [loadForceState],
  );

  const clearForce = useCallback(
    () => writeMymConfig((item) => ({ ...item, forceModal: false })),
    [writeMymConfig],
  );

  const returnSpectateToStart = useCallback(async () => {
    try {
      await fetch(`${SP_BASE}/return-to-start`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({}),
      });
    } catch {
      /* spectate stays on the end screen until the host retries */
    }
  }, []);

  const runBusy = useCallback(async (fn, fallbackMessage) => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(e.message || fallbackMessage);
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  const forceJoin = useCallback(
    () =>
      runBusy(async () => {
        await writeMymConfig((item) => {
          const isHol = item.id === "higher_or_lower";
          return {
            ...item,
            forceModal: isHol,
            enabled: isHol ? true : !!item.enabled,
            hidden: isHol ? false : !!item.hidden,
          };
        });
        await loadSession();
      }, "Failed to force join users"),
    [runBusy, writeMymConfig, loadSession],
  );

  const forceStop = useCallback(
    () =>
      runBusy(async () => {
        await clearForce();
        await loadSession();
      }, "Failed to stop forced join"),
    [runBusy, clearForce, loadSession],
  );

  /** End Game: server finalizes scores and broadcasts game over; returns the final results. */
  const endGame = useCallback(async () => {
    const endingId = sessionIdRef.current;
    const data = await runBusy(async () => {
      const res = await fetch(`${SP_BASE}/end`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Failed to end the game");
      return body;
    }, "Failed to end the game");
    if (!data) return null;
    musicRef.current.stop?.();
    const results = data.final_results || emptyResults(data.session_id || endingId);
    setFinalResults(results);
    clearLiveState();
    return results;
  }, [runBusy, clearLiveState]);

  const playClue = useCallback(
    async (part) => {
      if (clueLoading || !sessionIdRef.current) return;
      setClueLoading(true);
      setError(null);
      try {
        const res = await fetch(`${SP_BASE}/clue`, {
          method: "POST",
          headers: jsonHeaders(),
          body: JSON.stringify({ part }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) throw new Error(data.error || "Could not play clue");
        await playClueNote(data.note, soundsRef.current);
      } catch (e) {
        setError(e.message);
      } finally {
        setClueLoading(false);
      }
    },
    [clueLoading],
  );

  const refresh = useCallback(() => {
    loadSession();
    loadForceState();
  }, [loadSession, loadForceState]);

  /** Dismiss the final results; a late `holsp:session_ended` for the same session won't reopen them. */
  const clearFinalResults = useCallback(() => {
    setFinalResults((prev) => {
      if (prev?.session_id != null) dismissedResultsRef.current.add(prev.session_id);
      return null;
    });
  }, []);

  return {
    loading,
    busy,
    error,
    setError,
    session,
    joiners,
    history,
    leaderboard,
    numberOfCards,
    forceActive,
    hotSeatAnnounce,
    closeHotSeatAnnounce: () => setHotSeatAnnounce(null),
    clueLoading,
    countdownEndsAt,
    clearCountdown,
    finalResults,
    clearFinalResults,
    loadSession,
    refresh,
    beginPrepMusic: () => musicRef.current.beginPrep?.(),
    stopHostMusic: () => musicRef.current.stop?.(),
    action,
    startSession,
    setClueUnlocked: (unlocked) => action("clue-access", { unlocked: !!unlocked }),
    setLobbyPhase,
    returnSpectateToStart,
    restartSession: startSession,
    endGame,
    forceJoin,
    forceStop,
    playClue,
  };
};

export default useHolHostSession;
